import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MessageSquare, ArrowRight, Loader2, Mail, Lock, Sparkles, ArrowLeft, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';
  const [mode, setMode] = useState(initialMode); // 'login', 'signup', 'magic', 'forgot', 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const navigate = useNavigate();
  const { login, signup, sendMagicLink, verifyMagicLink, user } = useAuth();

  // Handle magic link verification
  useEffect(() => {
    const token = searchParams.get('token');
    const path = window.location.pathname;
    
    // Check if this is a password reset token
    if (path.includes('reset-password') && token) {
      setResetToken(token);
      setMode('reset');
      // Verify the token is valid
      const verifyToken = async () => {
        try {
          const res = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
          setEmail(res.data.email);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Invalid or expired reset link');
          navigate('/auth');
        }
      };
      verifyToken();
      return;
    }
    
    // Handle magic link token
    if (token && !path.includes('reset-password')) {
      const verify = async () => {
        setLoading(true);
        try {
          const userData = await verifyMagicLink(token);
          toast.success('Signed in successfully!');
          if (!userData.onboarding_completed) {
            navigate('/onboarding');
          } else {
            navigate('/chat');
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Invalid or expired link');
          navigate('/auth');
        } finally {
          setLoading(false);
        }
      };
      verify();
    }
  }, [searchParams, verifyMagicLink, navigate]);

  // Update mode when query param changes
  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (modeParam === 'signup') {
      setMode('signup');
    }
  }, [searchParams]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (!user.onboarding_completed) {
        navigate('/onboarding');
      } else {
        navigate('/chat');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email && mode !== 'reset') {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'magic') {
        await sendMagicLink(email);
        setMagicLinkSent(true);
        toast.success('Check your email for the sign-in link');
      } else if (mode === 'forgot') {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setResetEmailSent(true);
        toast.success('Check your email for the reset link');
      } else if (mode === 'reset') {
        if (!newPassword) {
          toast.error('Please enter a new password');
          setLoading(false);
          return;
        }
        if (newPassword.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        if (newPassword !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        await axios.post(`${API}/auth/reset-password`, { 
          token: resetToken, 
          new_password: newPassword 
        });
        setResetSuccess(true);
        toast.success('Password reset successfully!');
      } else if (mode === 'login') {
        if (!password) {
          toast.error('Please enter your password');
          setLoading(false);
          return;
        }
        const userData = await login(email, password);
        toast.success('Welcome back!');
        if (!userData.onboarding_completed) {
          navigate('/onboarding');
        } else {
          navigate('/chat');
        }
      } else if (mode === 'signup') {
        if (!password) {
          toast.error('Please enter a password');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        const userData = await signup(email, password);
        toast.success('Account created!');
        navigate('/onboarding');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  if (searchParams.get('token') && loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Verifying your link...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="relative inline-block">
              <div className="absolute -top-1.5 left-0.5 sm:-top-1 sm:left-0.5">
                <div className="bg-white text-background px-1.5 py-0.5 rounded text-[10px] sm:text-[8px] font-bold" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>
                  hey
                </div>
                <div className="absolute -bottom-0.5 left-2 sm:left-1.5 w-1.5 h-1.5 sm:w-1 sm:h-1 bg-white transform rotate-45"></div>
              </div>
              <span className="text-5xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>arlo</span>
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            {/* Reset Password Success */}
            {resetSuccess ? (
              <motion.div
                key="reset-success"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-[Manrope]">
                    Password reset!
                  </h1>
                  <p className="text-muted-foreground">
                    Your password has been successfully reset.
                  </p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5" />
                    <div className="text-sm text-emerald-600">
                      <p>You can now sign in with your new password.</p>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    setResetSuccess(false);
                    setMode('login');
                    setNewPassword('');
                    setConfirmPassword('');
                    navigate('/auth');
                  }}
                  className="w-full h-11 bg-foreground text-background hover:bg-foreground/90"
                  data-testid="go-to-login-btn"
                >
                  Sign in now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            ) : resetEmailSent ? (
              <motion.div
                key="reset-sent"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-[Manrope]">
                    Check your email
                  </h1>
                  <p className="text-muted-foreground">
                    We sent a password reset link to <span className="text-foreground font-medium">{email}</span>
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-accent mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p>Click the link in your email to reset your password. The link expires in 1 hour.</p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setResetEmailSent(false);
                    setMode('login');
                    setEmail('');
                  }}
                  className="text-muted-foreground"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to sign in
                </Button>
              </motion.div>
            ) : mode === 'reset' ? (
              <motion.div
                key="reset-form"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-8"
              >
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-[Manrope]">
                    Reset your password
                  </h1>
                  <p className="text-muted-foreground">
                    Enter a new password for <span className="text-foreground font-medium">{email}</span>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-10"
                        autoFocus
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-foreground text-background hover:bg-foreground/90"
                    disabled={loading}
                    data-testid="reset-password-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Reset Password
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>

                <Button
                  variant="ghost"
                  onClick={() => {
                    setMode('login');
                    navigate('/auth');
                  }}
                  className="text-muted-foreground w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to sign in
                </Button>
              </motion.div>
            ) : mode === 'forgot' ? (
              <motion.div
                key="forgot-form"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-8"
              >
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-[Manrope]">
                    Forgot password?
                  </h1>
                  <p className="text-muted-foreground">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        autoFocus
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-foreground text-background hover:bg-foreground/90"
                    disabled={loading}
                    data-testid="send-reset-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Send reset link
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>

                <Button
                  variant="ghost"
                  onClick={() => {
                    setMode('login');
                    setEmail('');
                  }}
                  className="text-muted-foreground w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to sign in
                </Button>
              </motion.div>
            ) : magicLinkSent ? (
              <motion.div
                key="magic-sent"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-[Manrope]">
                    Check your email
                  </h1>
                  <p className="text-muted-foreground">
                    We sent a sign-in link to <span className="text-foreground font-medium">{email}</span>
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-accent mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p>Click the link in your email to sign in. The link expires in 15 minutes.</p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setEmail('');
                  }}
                  className="text-muted-foreground"
                  data-testid="back-to-login-btn"
                >
                  Use a different email
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="auth-form"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="space-y-8"
              >
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-[Manrope]">
                    {mode === 'signup' ? 'Create your account' : 'Welcome back'}
                  </h1>
                  <p className="text-muted-foreground">
                    {mode === 'signup' 
                      ? 'Start using your AI work assistant'
                      : 'Sign in to continue to Arlo'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        autoFocus
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  {mode !== 'magic' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        {mode === 'login' && (
                          <button
                            type="button"
                            onClick={() => setMode('forgot')}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="forgot-password-link"
                          >
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type="password"
                          placeholder={mode === 'signup' ? 'Create a password' : 'Enter your password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10"
                          data-testid="password-input"
                        />
                      </div>
                    </motion.div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-11 bg-foreground text-background hover:bg-foreground/90"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {mode === 'magic' ? 'Send magic link' : mode === 'signup' ? 'Create account' : 'Sign in'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                {/* Alternative methods */}
                <div className="space-y-3">
                  {mode !== 'magic' && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11"
                      onClick={() => {
                        setMode('magic');
                        setPassword('');
                      }}
                      data-testid="magic-link-btn"
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      Sign in with magic link
                    </Button>
                  )}
                  
                  {mode === 'magic' && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11"
                      onClick={() => setMode('login')}
                      data-testid="password-login-btn"
                    >
                      <Lock className="mr-2 h-4 w-4" />
                      Sign in with password
                    </Button>
                  )}
                </div>

                {/* Toggle signup/login */}
                <p className="text-center text-sm text-muted-foreground">
                  {mode === 'signup' ? (
                    <>
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setMode('login')}
                        className="text-foreground hover:underline font-medium"
                        data-testid="switch-to-login"
                      >
                        Sign in
                      </button>
                    </>
                  ) : (
                    <>
                      Do not have an account?{' '}
                      <button
                        type="button"
                        onClick={() => setMode('signup')}
                        className="text-foreground hover:underline font-medium"
                        data-testid="switch-to-signup"
                      >
                        Create one
                      </button>
                    </>
                  )}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="mt-12 flex gap-4 text-xs text-muted-foreground">
            <button onClick={() => navigate('/privacy')} className="hover:text-foreground transition-colors">Privacy</button>
            <button onClick={() => navigate('/terms')} className="hover:text-foreground transition-colors">Terms</button>
          </div>

          {/* Terms agreement notice for signup */}
          {mode === 'signup' && (
            <p className="mt-4 text-xs text-muted-foreground text-center max-w-xs">
              By creating an account, you agree to our{' '}
              <button onClick={() => navigate('/terms')} className="underline hover:text-foreground">Terms of Use</button>
              {' '}and{' '}
              <button onClick={() => navigate('/privacy')} className="underline hover:text-foreground">Privacy Policy</button>.
            </p>
          )}
        </div>
      </div>

      {/* Right side - Image (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1542342061607-904c53a454c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1pbmltYWwlMjBnZW9tZXRyaWMlMjBhcmNoaXRlY3R1cmUlMjBmb2N1c3xlbnwwfHx8fDE3Njk5MDMzOTd8MA&ixlib=rb-4.1.0&q=85"
          alt="Abstract architecture"
          className="absolute inset-0 w-full h-full object-cover grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/20 to-transparent" />
      </div>
    </div>
  );
};

export default AuthPage;
